import React, { useState } from "react";
import Dialog from "carbon-react/lib/components/dialog";
import Heading from "carbon-react/lib/components/heading";
import StepHeading from "../../../../common/stepHeading/StepHeading";
import ErrorMessage from "../../../../messages/ErrorMessage";
import {
  StepSequence,
  StepSequenceItem,
} from "carbon-react/lib/components/step-sequence";
import ConfirmDialog from "../../../../common/confirmDialog/ConfirmDialog";
import SendCodeStep from "./SendCodeStep";
import EnterCodeStep from "./EnterCodeStep";
import MfaEnrollmentsDeletedStep from "./MfaEnrollmentsDeletedStep";
import { useAppContext } from "../../../../../contexts/AppContext";
import { useUserApi } from "../../../../../services/UserApi";

import "../deleteMfaEnrollmentDialog/DeleteMfaEnrollmentDialog.css";

const steps = Object.freeze({
  sendCode: "sendCode",
  enterCode: "enterCode",
  accessVerified: "accessVerified",
});

const getInitialSendCodeState = () => {
  return {
    languageCode: null,
    languageCodeValid: true,
    agentComments: "",
    agentCommentsValid: true,
  };
};

const getInitialEnterCodeState = () => {
  return {
    verificationCode: "",
    verificationCodeValid: true,
  };
};
const ResetMfaForUserDialog = ({ open, onClose, user, onResetMFA }) => {
  const [busy, setBusy] = useState(false);
  const [currentStep, setCurrentStep] = useState(steps.sendCode);
  const [sendCodeState, setSendCodeState] = useState(getInitialSendCodeState());
  const [enterCodeState, setEnterCodeState] = useState(
    getInitialEnterCodeState()
  );
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [confirmCloseDialogOpen, setConfirmCloseDialogOpen] = useState(false);

  const { toastSuccess } = useAppContext();
  const { sendEmailAccessVerificationCode, deleteAllMfaEnrollments } =
    useUserApi();

  const onConfirmCloseDialogCancel = () => {
    setConfirmCloseDialogOpen(false);
  };

  const onConfirmCloseDialogAccept = () => {
    setConfirmCloseDialogOpen(false);
    onDialogClose();
  };

  const onDialogClose = () => {
    onClose();
    resetState();
  };

  const clearDialogError = () => {
    setErrorMsg(null);
    setShowError(false);
  };

  const showDialogError = (msg) => {
    const sanistizedMsg =
      msg && msg.length > 0 ? msg.trim() : "Operation failed";
    setErrorMsg(sanistizedMsg);
    setShowError(true);
  };

  const resetState = () => {
    setConfirmCloseDialogOpen(false);
    clearDialogError();
    setSendCodeState(getInitialSendCodeState());
    setEnterCodeState(getInitialEnterCodeState());
    setCurrentStep(steps.sendCode);
  };

  const onDialogCancel = () => {
    if (busy) return;
    if (unsavedDataPresent()) {
      setConfirmCloseDialogOpen(true);
      return;
    }

    onClose();
  };

  const unsavedDataPresent = () => {
    return (
      (currentStep === steps.sendCode &&
        (stringHasValue(sendCodeState.languageCode) ||
          stringHasValue(sendCodeState.agentComments))) ||
      currentStep === steps.enterCode
    );
  };

  const stringHasValue = (value) => {
    return value && value.length > 0;
  };

  const onSendCodeStepCompleted = async () => {
    try {
      setBusy(true);
      clearDialogError();

      const apiResult = await sendEmailAccessVerificationCode(
        user.userId,
        sendCodeState.languageCode,
        sendCodeState.agentComments
      );

      if (!apiResult?.success)
        throw new Error(
          apiResult?.error?.description || "Failed to send verification code"
        );

      toastSuccess(
        `Email access verification requested. Verification code sent to ${user?.email}`
      );

      setCurrentStep(steps.enterCode);
    } catch (error) {
      showDialogError(error?.toString());
    }
    setBusy(false);
  };

  const onEnterCodeStepCompleted = async () => {
    try {
      setBusy(true);
      clearDialogError();

      var apiResult = await deleteAllMfaEnrollments(
        user?.userId,
        enterCodeState.verificationCode,
        sendCodeState.languageCode,
        sendCodeState.agentComments
      );

      if (!apiResult?.success)
        throw new Error(
          apiResult?.error?.description || "Failed to delete mfa enrollments"
        );

      toastSuccess(
        `MFA enrollments for ${user?.email} has been successfully deleted.`
      );

      setCurrentStep(steps.accessVerified);

      await onResetMFA();
    } catch (error) {
      showDialogError(error?.toString());
    }
    setBusy(false);
  };

  const onEmailAccessVerifiedStepCompleted = () => {
    onDialogClose();
  };

  const heading = <Heading title="Reset MFA" />;

  const stepSequence = (
    <StepSequence
      className="verify-email-access-step-sequence"
      orientation="horizontal"
    >
      <StepSequenceItem
        aria-label="Step 1 of 3"
        hiddenCompleteLabel="Complete"
        hiddenCurrentLabel="Current"
        indicator="1"
        status={currentStep === steps.sendCode ? "current" : "complete"}
      >
        Select language and send code
      </StepSequenceItem>
      <StepSequenceItem
        aria-label="Step 2 of 3"
        hiddenCompleteLabel="Complete"
        hiddenCurrentLabel="Current"
        indicator="2"
        status={
          currentStep === steps.sendCode
            ? "incomplete"
            : currentStep === steps.enterCode
            ? "current"
            : "complete"
        }
      >
        Enter code
      </StepSequenceItem>
      <StepSequenceItem
        aria-label="Step 3 of 3"
        hiddenCompleteLabel="Complete"
        hiddenCurrentLabel="Current"
        indicator="3"
        status={
          currentStep === steps.accessVerified ? "complete" : "incomplete"
        }
      >
        MFA enrollments deleted
      </StepSequenceItem>
    </StepSequence>
  );

  const stepHeading = (
    <StepHeading label="Current email address" value={user?.email} />
  );

  const errorMessage = (
    <>
      {showError !== true ? null : (
        <ErrorMessage
          message={
            errorMsg && errorMsg.length > 0 ? errorMsg : "The operation failed!"
          }
        />
      )}
    </>
  );

  const sendCodeStep = (
    <SendCodeStep
      user={user}
      busy={busy}
      state={sendCodeState}
      onStateChanged={setSendCodeState}
      onCancel={onDialogCancel}
      onCompleted={onSendCodeStepCompleted}
    />
  );

  const enterCodeStep = (
    <EnterCodeStep
      user={user}
      busy={busy}
      state={enterCodeState}
      onStateChanged={setEnterCodeState}
      onCancel={onDialogCancel}
      onCompleted={onEnterCodeStepCompleted}
    />
  );

  const emailAccessVerifiedStep = (
    <MfaEnrollmentsDeletedStep
      busy={busy}
      user={user}
      onCompleted={onEmailAccessVerifiedStepCompleted}
    />
  );

  const stepBody = (
    <>
      {currentStep === steps.sendCode
        ? sendCodeStep
        : currentStep === steps.enterCode
        ? enterCodeStep
        : emailAccessVerifiedStep}
    </>
  );

  return (
    <>
      {!user ? null : (
        <>
          <Dialog open={open} showCloseIcon={true} onCancel={onDialogCancel}>
            {heading}
            {stepSequence}
            {stepHeading}
            {stepBody}
            {errorMessage}
          </Dialog>
          <ConfirmDialog
            open={confirmCloseDialogOpen}
            prompt={
              currentStep === steps.sendCode
                ? "There is unsaved data present, are you sure you want to close?"
                : "The verification process is incomplete are you sure you want to abandon this?"
            }
            confirmBtnLabel={
              currentStep === steps.sendCode ? "Close" : "Abandon"
            }
            onConfirm={onConfirmCloseDialogAccept}
            onCancel={onConfirmCloseDialogCancel}
          />
        </>
      )}
    </>
  );
};

export default ResetMfaForUserDialog;
